import React from 'react';
import type { OrgSwitcherItem, OrgStatus } from '../types/organization';

export const ORG_STORAGE_KEY = 'redbax_current_org';

interface OrgContextValue {
  currentOrg: OrgSwitcherItem | null;
  setCurrentOrg: (org: OrgSwitcherItem) => void;
  // The current org's lifecycle status. Seeded from the selected org (bootstrap /
  // switcher) and updated live by useOrgStatusSync on suspend/reactivate.
  orgStatus: OrgStatus | null;
  setOrgStatus: (status: OrgStatus) => void;
  // Forget the selected org entirely, so the client stops asserting it.
  // Called when the BFF refuses the org (org-forbidden), never when it is merely suspended.
  clearCurrentOrg: () => void;
}

const OrgContext = React.createContext<OrgContextValue>({
  currentOrg: null,
  setCurrentOrg: () => {},
  orgStatus: null,
  setOrgStatus: () => {},
  clearCurrentOrg: () => {},
});

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const [currentOrg, setCurrentOrgState] = React.useState<OrgSwitcherItem | null>(null);
  const [orgStatus, setOrgStatusState] = React.useState<OrgStatus | null>(null);

  const setCurrentOrg = React.useCallback((org: OrgSwitcherItem) => {
    setCurrentOrgState(org);
    // Seed status from the selected org so a suspended org shows its banner on load
    // and switching orgs reflects the new org's status.
    if (org.status) {
      setOrgStatusState(org.status);
    }
    localStorage.setItem(ORG_STORAGE_KEY, org.orgId);
  }, []);

  const setOrgStatus = React.useCallback((status: OrgStatus) => {
    setOrgStatusState(status);
  }, []);

  // Drop both the in-memory selection and the persisted one. Dropping only localStorage would leave
  // the current session still attaching X-Redbax-Org-Id for an org the BFF has already refused, so the
  // user would stay locked out until a reload.
  const clearCurrentOrg = React.useCallback(() => {
    setCurrentOrgState(null);
    setOrgStatusState(null);
    localStorage.removeItem(ORG_STORAGE_KEY);
  }, []);

  const value = React.useMemo(
    () => ({ currentOrg, setCurrentOrg, orgStatus, setOrgStatus, clearCurrentOrg }),
    [currentOrg, setCurrentOrg, orgStatus, setOrgStatus, clearCurrentOrg],
  );

  return React.createElement(OrgContext.Provider, { value }, children);
}

export function useCurrentOrgContext() {
  return React.useContext(OrgContext);
}
